import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Trophy, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useParams, useNavigate } from "react-router-dom";

const GoalCompletionModal = ({ isOpen, onClose, revenueGoal, onNewGoal }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [showNewGoalInput, setShowNewGoalInput] = useState(false);
  const [newGoal, setNewGoal] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSetNewGoal = async () => {
    if (!newGoal || isNaN(parseFloat(newGoal)) || parseFloat(newGoal) < 1000) {
      toast.error("Please enter a valid revenue goal (minimum $1,000)");
      return;
    }

    setIsSubmitting(true);
    try {
      const goalAmount = parseFloat(newGoal);
      
      // Update user's revenue goal
      await axios.put(`${API}/users/${userId}`, {
        revenueGoal: goalAmount,
        currentRevenue: 0  // Reset revenue for new goal
      });

      toast.success(`New revenue goal set: $${goalAmount.toLocaleString()}`);
      
      if (onNewGoal) {
        onNewGoal(goalAmount);
      }
      
      // Refresh the page to show new goal
      window.location.reload();
    } catch (error) {
      console.error("Error setting new goal:", error);
      toast.error("Failed to set new goal");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <Card className="relative w-full max-w-xl bg-gradient-to-br from-[#0f1419] to-[#1a1f24] border-[#3dd9c5]/50 p-8 shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Celebration Graphics */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Trophy className="h-20 w-20 text-[#3dd9c5] animate-bounce" />
            <Sparkles className="absolute -top-2 -right-2 h-8 w-8 text-yellow-400 animate-pulse" />
            <Sparkles className="absolute -bottom-2 -left-2 h-6 w-6 text-yellow-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-4xl font-bold text-white text-center mb-2">
          🎉 Congratulations!
        </h2>
        <p className="text-center text-xl text-[#3dd9c5] font-semibold mb-4">
          Goal Achieved!
        </p>
        <p className="text-center text-gray-300 mb-8 text-lg">
          You've reached <span className="text-white font-bold">${(revenueGoal / 1000000).toFixed(1)}M</span> in revenue!
        </p>

        {/* Decorative elements */}
        <div className="flex justify-center gap-8 mb-8">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#3dd9c5]/20 flex items-center justify-center mb-2 mx-auto">
              <Target className="h-8 w-8 text-[#3dd9c5]" />
            </div>
            <p className="text-sm text-gray-400">Mission Complete</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-yellow-400/20 flex items-center justify-center mb-2 mx-auto">
              <Trophy className="h-8 w-8 text-yellow-400" />
            </div>
            <p className="text-sm text-gray-400">Success!</p>
          </div>
        </div>

        {/* New Goal Section */}
        {!showNewGoalInput ? (
          <div className="text-center">
            <p className="text-gray-300 mb-6">Ready to set a new revenue goal?</p>
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 bg-transparent border-gray-700 text-white hover:bg-white/10"
              >
                Close
              </Button>
              <Button
                onClick={() => setShowNewGoalInput(true)}
                className="flex-1 bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
              >
                Set New Goal
              </Button>
            </div>
          </div>
        ) : (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <p className="text-white text-center mb-4">Enter your new revenue goal:</p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl text-gray-400">$</span>
              <Input
                type="number"
                placeholder="1000000"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                className="flex-1 bg-white/5 border-[#3dd9c5]/50 text-white text-xl placeholder:text-gray-500 focus:border-[#3dd9c5]"
                min="1000"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowNewGoalInput(false)}
                variant="outline"
                className="flex-1 bg-transparent border-gray-700 text-white hover:bg-white/10"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSetNewGoal}
                className="flex-1 bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Setting..." : "Set Goal"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default GoalCompletionModal;
